import { FC, FormEvent, ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import type { ActivityResponse, Feed, FeedsClient } from '@stream-io/feeds-client'
import { useActivityComments, useClientConnectedUser } from '@stream-io/feeds-client/react-bindings'

import { Avatar, Button, Textarea } from '@/components'
import { EnableNotificationsBanner, IconButton } from '@/compositions'
import { trans } from '@/helpers/trans'

import { CommentListItem } from './subcomponents'

import './CommentsSection.scss'

// Oldest first, so a thread reads top-to-bottom and your new comment lands right above the input.
const COMMENT_SORT = 'first'

interface CommentsSectionProps {
    feed: Feed
    activity: ActivityResponse
    client: FeedsClient
}

export const CommentsSection: FC<CommentsSectionProps> = ({ feed, activity, client }): ReactElement => {
    const connectedUser = useClientConnectedUser()

    const {
        comments: rawComments, has_next_page: hasNextPage, is_loading_next_page: isLoadingNextPage, loadNextPage,
    } = useActivityComments({ feed, activity })

    // The SDK appends a new comment both from the addComment HTTP response and from the
    // `feeds.comment.added` WS echo, and only dedupes the two when the feed is watched and
    // the event is matched to the request - it never checks ids. Dedupe here so our own
    // comment can't be rendered twice.
    const comments = useMemo(() => rawComments?.filter(
        (comment, index) => rawComments.findIndex(other => other.id === comment.id) === index,
    ), [rawComments])

    const hasLoadedRef = useRef(false)

    useEffect(() => {
        if (! hasLoadedRef.current) {
            hasLoadedRef.current = true
            loadNextPage({ sort: COMMENT_SORT })
        }
    }, [loadNextPage])

    const [draft, setDraft] = useState('')
    const [sending, setSending] = useState(false)
    const [hasCommented, setHasCommented] = useState(false)

    const submitComment = async (): Promise<void> => {
        const text = draft.trim()

        if (! text || sending) {
            return
        }

        setSending(true)

        try {
            await client.addComment({ object_id: activity.id, object_type: 'activity', comment: text })
            setDraft('')
            setHasCommented(true)
        } finally {
            setSending(false)
        }
    }

    const handleSubmit = (event: FormEvent): void => {
        event.preventDefault()
        submitComment()
    }

    const handleSaveComment = async (commentId: string, text: string): Promise<void> => {
        await client.updateComment({ id: commentId, comment: text })
    }

    const handleDeleteComment = async (commentId: string): Promise<void> => {
        await client.deleteComment({ id: commentId })
    }

    return (
        <div className="comments-section">
            {comments === undefined || (isLoadingNextPage && ! comments.length) ? null : (
                <>
                    {! comments.length ? (
                        <p className="comments-section__empty">
                            {trans('containers.newsfeedContainer.noCommentsFound')}
                        </p>
                    ) : (
                        <ul className="comments-section__list">
                            {comments.map(comment => (
                                <li key={comment.id}>
                                    <CommentListItem
                                        comment={comment}
                                        isOwnComment={!! connectedUser && connectedUser.id === comment.user.id}
                                        onSave={handleSaveComment}
                                        onDelete={handleDeleteComment}
                                    />
                                </li>
                            ))}
                        </ul>
                    )}

                    {hasNextPage && (
                        <Button
                            text={trans('containers.newsfeedContainer.loadMoreComments')}
                            className="comments-section__load-more"
                            onClick={() => loadNextPage({ sort: COMMENT_SORT })}
                        />
                    )}
                </>
            )}

            {hasCommented && <EnableNotificationsBanner context="newsfeed" className="comments-section__notifications-cta" />}

            <form className="comments-section__input-row" onSubmit={handleSubmit}>
                <div className="comments-section__input-main">
                    <Avatar
                        image={connectedUser?.image}
                        initials={connectedUser?.name?.charAt(0)}
                        name={connectedUser?.name}
                        className="comments-section__input-avatar"
                    />
                    <Textarea
                        placeholder={trans('containers.newsfeedContainer.writeComment')}
                        value={draft}
                        autoGrow
                        minRows={1}
                        maxRows={6}
                        spellCheck
                        autoCorrect="on"
                        autoCapitalize="sentences"
                        onChange={event => setDraft(event.target.value)}
                        onSubmit={submitComment}
                        className="comments-section__input"
                    />
                </div>
                <IconButton
                    hideLabel
                    type="submit"
                    icon="arrow"
                    text={trans('containers.newsfeedContainer.send')}
                    className="comments-section__submit"
                    iconClassName="comments-section__submit-icon"
                    disabled={! draft.trim() || sending}
                />
            </form>
        </div>
    )
}
